/**
 * Development-time frame validation against the vendored protocol schema (handoff B1).
 *
 * WHAT IT IS FOR. The reducer is deliberately tolerant: an unknown event type is ignored, a missing
 * field reads as absent, a malformed frame is dropped. That is correct for production — a client
 * must not fall over because the server grew a field — but it means a genuine contract violation
 * arrives silently and shows up later as a blank panel nobody can explain. This turns that silence
 * into a loud console error in development, and into nothing at all in production.
 *
 * IT NEVER CHANGES BEHAVIOUR. Validation is observation only: `validateFrame` reports, and no caller
 * may drop, alter or desync on its verdict. The reason is in §1.4 — the recovery path for bad state
 * is `state.snapshot`/`GET /state`, and a client that refused frames its schema disliked would
 * diverge from a server that is, by contract, authoritative. A stricter client is not a safer one.
 *
 * PRODUCTION COST: ZERO, MEASURED. Three facts, each checked rather than reasoned about:
 *
 *   - `rspack build` IS A PRODUCTION BUILD. The rspack CLI defaults `mode` to production and sets
 *     `process.env.NODE_ENV=production` before this repo's config is evaluated, so `pnpm build`
 *     produces a production bundle even though `.env` says `NODE_ENV=development`. (An earlier note
 *     here claimed the opposite and the measurements taken under it were wrong.)
 *   - THIS MODULE'S CODE IS GONE from `main.js`: no violation message, no
 *     validation-unavailable warning. `FRAME_VALIDATION_ENABLED` is a DefinePlugin literal, so
 *     `if (!FRAME_VALIDATION_ENABLED) return null` folds and the rest is unreachable.
 *   - AJV IS GONE TOO, and this took a build change. The dynamic `import("ajv/dist/2020")` cost
 *     121 KiB in the EAGERLY loaded `vendors.js`, because rspack builds the module graph before
 *     eliminating the dead branch and the `vendor` cache group uses `chunks: "all"` — which pulls
 *     node_modules out of async chunks into the eager bundle. A dev-only validator was shipping a
 *     JSON-Schema compiler to every user on first paint. `rspack.config.cjs` now aliases
 *     `ajv/dist/2020` to `src/stubs/empty.js` when `isProd`, and `vendors.js` is then
 *     byte-identical to a build with this module's import deleted outright: 16,929,972 bytes,
 *     against 17,053,939 before.
 *
 * So the validator runs under `rspack serve` and under vitest, and is absent from anything
 * `rspack build` emits. The import stays DYNAMIC regardless: a static one would put the compiler in
 * `main.js`, where no alias applies. Compilation is lazy and cached — the first frame pays, later
 * frames are a function call.
 *
 * ONE ERROR PER SHAPE, NOT PER FRAME. A run emits thousands of frames and a violation is almost
 * always systematic — one field wrong on every `message.part_delta`. Logging each would bury the
 * console and hide the second, different violation. `seenShapes` keeps the first of each kind.
 */

import { EVENT_TYPES } from "./event-types";

/**
 * Build-time constant, replaced by rspack's `DefinePlugin`. Kept as a named export so a test can
 * assert the production branch is the dead one rather than inferring it.
 */
export const FRAME_VALIDATION_ENABLED = process.env.NODE_ENV !== "production";

export interface FrameViolation {
  /** `event_type` when the frame had a readable one — otherwise `"<unknown>"`. */
  eventType: string;
  /** ajv's messages, already flattened to `path: message` and capped. */
  problems: string[];
}

/**
 * The envelope rules worth checking without ajv, so a frame that is not even an envelope reports
 * something useful instead of forty nested schema errors.
 *
 * Exported and pure because these are the checks that matter most and the ones a DOM-less test can
 * assert directly: everything below the envelope is the schema's job.
 */
export function checkEnvelope(frame: unknown): string[] {
  const problems: string[] = [];
  if (!frame || typeof frame !== "object" || Array.isArray(frame)) {
    return ["frame is not an object"];
  }
  const f = frame as Record<string, unknown>;
  const eventType = f.event_type;

  /*
   * TRANSPORT FRAMES FIRST, and the order is load-bearing.
   *
   * `heartbeat` and `subscribed` carry no `id:`, are not durable and are never replayed (§1.2,
   * §1.5) — so they legitimately have none of the envelope's required fields, and they are NOT in
   * `EVENT_TYPES`. That last part is the contract's own distinction: `EVENT_TYPES` is generated from
   * the schema's `x-eventMap`, which maps the 33 DURABLE events to their body definitions, while the
   * schema's `EventType` enum holds all 35 wire values. Checking the vocabulary before this
   * exemption reported every liveness ping as a contract violation.
   */
  if (eventType === "heartbeat" || eventType === "subscribed") return problems;

  if (typeof eventType !== "string") {
    problems.push("event_type: missing or not a string");
  } else if (!(EVENT_TYPES as readonly string[]).includes(eventType)) {
    // §4: the wire vocabulary is CLOSED. An unknown value is a contract violation, and the
    // reducer's tolerance means it would otherwise be indistinguishable from a frame it chose to
    // ignore.
    problems.push(
      `event_type: "${eventType}" is not one of the ${EVENT_TYPES.length} durable contract events`,
    );
  }

  if (typeof f.protocol_version !== "string") {
    problems.push("protocol_version: missing or not a string");
  } else if (!/^\d+\.\d+$/.test(f.protocol_version)) {
    problems.push(`protocol_version: "${f.protocol_version}" is not major.minor`);
  }
  if (typeof f.seq !== "number" || !Number.isFinite(f.seq)) problems.push("seq: missing or not a number");
  if (typeof f.run_id !== "string") problems.push("run_id: missing or not a string");
  if (typeof f.session_id !== "string") problems.push("session_id: missing or not a string");
  if (typeof f.ts !== "string") problems.push("ts: missing or not a string");
  // §1.3: "Never flattened into the envelope." A flattened frame is the legacy shape, and reading it
  // as v1 is the single most likely wiring mistake — worth naming explicitly.
  if (f.data === undefined || f.data === null || typeof f.data !== "object") {
    problems.push("data: missing or not an object (a flattened frame is the legacy shape)");
  }
  return problems;
}

// ── ajv, lazily and only in development ─────────────────────────────────────

type Validator = (data: unknown) => boolean;

let compiling: Promise<Map<string, Validator> | null> | null = null;
const seenShapes = new Set<string>();

/**
 * The event → body-definition map, READ FROM THE SCHEMA rather than written here.
 *
 * The schema's root is the ENVELOPE, whose `data` is an open `{"type":"object"}` — so validating a
 * frame against the root checks the envelope and nothing about the body. The bodies are named by the
 * contract's own `x-eventMap`, which pairs each of the 31 durable events with a `$defs` entry
 * (`ev.state.snapshot` → `RunState`, `ev.plan.updated` → an object requiring `plan`, and so on).
 *
 * THIS USED TO BE A HAND-WRITTEN TABLE OF THIRTEEN. It was written against a schema transcribed from
 * the contract prose, which carried no `x-eventMap` — so eighteen event bodies were envelope-checked
 * only, and the thirteen that were checked pointed at the WRAPPED type (`Plan` for `plan.updated`)
 * with an `at` field naming the property to reach into. That is the map the contract now ships, done
 * by hand and less completely. Reading it means every durable event is validated in full, and a
 * future event added upstream is covered the moment the schema is re-vendored rather than when
 * someone remembers to extend a table.
 *
 * Anything absent from the map is envelope-checked only — honest, and now empty in practice.
 */
function bodyRefsFrom(schema: unknown): Map<string, string> {
  const defs = (schema as { $defs?: Record<string, unknown> })?.$defs ?? {};
  const map = (defs["x-eventMap"] as { const?: Record<string, string> } | undefined)?.const;
  const out = new Map<string, string>();
  if (!map) return out;
  for (const [eventType, ref] of Object.entries(map)) {
    if (typeof ref === "string" && ref.startsWith("#/$defs/")) out.set(eventType, ref.slice("#/".length));
  }
  return out;
}

async function validators(): Promise<Map<string, Validator> | null> {
  if (!compiling) {
    compiling = (async () => {
      try {
        /*
         * `ajv/dist/2020`, NOT `"ajv"`. This is load-bearing and it was wrong.
         *
         * The schema declares `$schema: https://json-schema.org/draft/2020-12/schema`, and plain
         * `ajv`'s default export is the DRAFT-07 class: `addSchema` on a 2020-12 document throws
         * `no schema with key or ref "https://json-schema.org/draft/2020-12/schema"`. That throw
         * landed in the catch below, which warns once and returns `null` — so body validation was
         * silently disabled from the moment it was written, and every frame was envelope-checked
         * only. The failure mode of a validator that cannot compile is not a loud error; it is a
         * quiet pass, which is why this comment exists.
         */
        const [{ default: Ajv2020 }, schemaModule] = await Promise.all([
          import("ajv/dist/2020"),
          import("./weave_protocol_v1.schema.json"),
        ]);
        const Ajv = Ajv2020;
        const schema = (schemaModule as { default?: unknown }).default ?? schemaModule;
        // `strict: false` because the vendored schema carries `format` and `$ref` shapes ajv's strict
        // mode rejects on principle rather than on correctness; `allErrors` so one frame reports
        // every problem rather than only the first.
        const ajv = new Ajv({ strict: false, allErrors: true, validateFormats: false });
        ajv.addSchema(schema as object, "weave");
        const map = new Map<string, Validator>();
        for (const [eventType, ref] of bodyRefsFrom(schema)) {
          const validate = ajv.compile({ $ref: `weave#/${ref}` });
          map.set(eventType, (data: unknown) => {
            const ok = validate(data) as boolean;
            if (!ok) {
              (validate as unknown as { __errors?: unknown }).__errors = validate.errors;
            }
            return ok;
          });
          map.set(`${eventType}__errors`, (() => validate.errors) as unknown as Validator);
        }
        return map;
      } catch (err) {
        // A validator that cannot compile must not break the app. Reported once, then silent.
        console.warn("[weave] frame validation unavailable:", err);
        return null;
      }
    })();
  }
  return compiling;
}

/**
 * Validate one frame and report the first instance of each violation shape. Never throws, never
 * returns a verdict a caller is expected to act on.
 *
 * Returns the violation for a test to assert on; callers ignore it.
 */
export function validateFrame(frame: unknown): FrameViolation | null {
  if (!FRAME_VALIDATION_ENABLED) return null;

  const envelopeProblems = checkEnvelope(frame);
  const eventType =
    frame && typeof frame === "object" && typeof (frame as { event_type?: unknown }).event_type === "string"
      ? (frame as { event_type: string }).event_type
      : "<unknown>";

  if (envelopeProblems.length) {
    report(eventType, envelopeProblems);
    // The body cannot be meaningfully checked against a broken envelope.
    return { eventType, problems: envelopeProblems };
  }

  // Body validation is async by nature (the lazy import), so it reports out of band. The envelope
  // result above is what a synchronous caller gets, which is the part worth blocking on.
  void validateBody(frame, eventType);
  return null;
}

async function validateBody(frame: unknown, eventType: string): Promise<void> {
  const map = await validators();
  if (!map) return;
  const validate = map.get(eventType);
  if (!validate) return;

  /*
   * The WHOLE `data` object, not a property of it.
   *
   * The `ev.*` definitions describe the envelope's `data` in full — `ev.plan.updated` requires
   * `plan` and permits `revision`/`diff_from_version`. The old hand-written table pointed at the
   * wrapped type instead and reached in with an `at` field, which validated `data.plan` and left
   * `data`'s own shape unchecked: a `plan.updated` carrying a valid plan under the wrong key
   * passed.
   */
  const data = (frame as { data?: unknown }).data;
  if (data === undefined) return;
  if (validate(data)) return;

  const errorsFn = map.get(`${eventType}__errors`) as unknown as () => { instancePath?: string; message?: string }[] | null;
  const errors = errorsFn?.() ?? [];
  report(
    eventType,
    errors.slice(0, 8).map((e) => `${e.instancePath || "/"}: ${e.message ?? "invalid"}`),
  );
}

/** One console error per violation SHAPE, so a systematic fault cannot bury a second one. */
function report(eventType: string, problems: string[]): void {
  const shape = `${eventType}::${problems.join("|")}`;
  if (seenShapes.has(shape)) return;
  seenShapes.add(shape);
  console.error(
    `[weave] frame does not match the protocol schema — ${eventType}\n` +
      problems.map((p) => `  • ${p}`).join("\n") +
      "\n  This is a CONTRACT violation, not a parse error. The reducer tolerates it; the backend and " +
      "the vendored schema disagree.",
  );
}

/** Test-only: forget which shapes have been reported. */
export function __resetFrameValidationForTests(): void {
  seenShapes.clear();
  compiling = null;
}
